import { createSlice } from "@reduxjs/toolkit";
import { apiThunk } from "App/store/apiThunk";

/**
 * The supplier dashboard — one call to the booking service
 * (GET /supplier-bookings/dashboard): now, today, month to date vs last
 * month, 14-day trend, top drivers, live / upcoming rides, fleet, attention.
 */

export interface MoneyTotals {
  rides: number;
  fare: number;
  licensed_taxi: number;
  third_party: number;
  supplier_commission: number;
  driver_net: number;
}

export interface DashboardData {
  generated_at: string;
  currency: string | null;
  commission_ready: boolean;
  now: {
    in_progress: number;
    finding_driver: number;
    awaiting_payment: number;
    drivers: { total: number; available: number; on_ride: number; offline: number };
  };
  today: MoneyTotals & { cancelled: number; not_assigned: number };
  month: MoneyTotals & { your_rides: number; platform_rides: number };
  previous: MoneyTotals & { to: string };
  trend: { date: string; rides: number; fare: number; supplier_commission: number }[];
  top_drivers: { driver_id: string; name: string; rides: number; fare: number; driver_net: number }[];
  live_rides: { id: number; booking_uni_code: string; status: string; rider_name: string; driver_name: string | null; offered_to: string | null; pickup: any; drop: any }[];
  upcoming: { id: number; schedule_uni_code: string; booking_time: string; rider_name: string; driver_name: string | null; pickup: any }[];
  fleet: { drivers: Record<string, number>; vehicles: Record<string, number> };
  attention: { severity: "error" | "warning" | "info"; text: string; link: string }[];
}

export const loadDashboard = apiThunk<any, void>("dashboard/load", {
  server: "taxi",
  url: "supplier-bookings/dashboard",
  method: "GET",
});

interface DashboardState {
  data: DashboardData | null;
  errors: any;
  loading: boolean;
}

const initialState: DashboardState = {
  data: null,
  errors: null,
  loading: false,
};

const dashboardSlice = createSlice({
  name: "dashboard",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(loadDashboard.pending, (state) => {
        state.loading = true;
        state.errors = null;
      })
      .addCase(loadDashboard.fulfilled, (state, action) => {
        state.loading = false;
        state.data = action.payload?.data ?? null;
      })
      .addCase(loadDashboard.rejected, (state, action) => {
        state.loading = false;
        state.errors = action.payload;
      });
  },
});

export default dashboardSlice.reducer;
